import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LogIn, LogOut, MapPin } from 'lucide-react';
import {
  ATTENDANCE_COPY,
  ATTENDANCE_STATUS_LABELS,
  EMPTY_STATES,
  GUARDIAN_RELATION_LABELS,
  PICKUP_LOCATION_COPY,
  formatDistance,
  mapLink,
} from '@saesak/shared';
import type { AttendanceStatus } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Avatar, Button, Card, Chip, EmptyState, ErrorNote, Input, Spinner } from '@/components/ui';
import { apiData } from '@/lib/api';
import { formatRelative, formatTime, todayISO } from '@/lib/format';
import { useCanWrite } from '@/store/auth';
import type { ActivePickupTrip, AttendanceRecord, ClassRoomSummary } from '@/types';

interface DailyBoard {
  date: string;
  classRoomId: string;
  rows: {
    child: { id: string; name: string; photoKey: string | null; allergies: string[] };
    attendance: AttendanceRecord | null;
    status: AttendanceStatus;
  }[];
}

/** 출석부 — 명세서 §5.8 (교사 탭 방식 등하원 기록) */
export function AttendancePage() {
  const queryClient = useQueryClient();
  const canWrite = useCanWrite();
  const [date, setDate] = useState(todayISO());
  const [classRoomId, setClassRoomId] = useState('');

  const rooms = useQuery({
    queryKey: ['classrooms'],
    queryFn: () => apiData<ClassRoomSummary[]>('/classrooms'),
  });

  const effectiveRoomId = classRoomId || rooms.data?.[0]?.id || '';

  const board = useQuery({
    queryKey: ['attendance', 'daily', effectiveRoomId, date],
    queryFn: () =>
      apiData<DailyBoard>(`/attendance/daily?classRoomId=${effectiveRoomId}&date=${date}`),
    enabled: Boolean(effectiveRoomId),
    placeholderData: keepPreviousData,
  });

  const check = useMutation({
    mutationFn: ({ childId, kind }: { childId: string; kind: 'in' | 'out' }) =>
      apiData(`/attendance/check-${kind}`, { method: 'POST', body: { childId, date } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['attendance'] });
      void queryClient.invalidateQueries({ queryKey: ['home'] });
    },
  });

  const setStatus = useMutation({
    mutationFn: ({ childId, status }: { childId: string; status: AttendanceStatus }) =>
      apiData('/attendance/status', { method: 'POST', body: { childId, date, status } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['attendance'] });
      void queryClient.invalidateQueries({ queryKey: ['home'] });
    },
  });

  if (rooms.isPending) return <Spinner />;

  return (
    <Screen withNav={false}>
      <AppBar title="출석부" back />

      <div className="flex flex-col gap-3 p-4">
        <Card className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-1.5">
            {rooms.data?.map((room) => (
              <Chip
                key={room.id}
                tone={room.id === effectiveRoomId ? 'primary' : 'neutral'}
                selected={room.id === effectiveRoomId}
                onClick={() => setClassRoomId(room.id)}
              >
                {room.name}
              </Chip>
            ))}
          </div>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Card>

        {/* 등하원 위치 공유 (§5.10 v1.22) — 보호자가 동의하고 켠 동안만 보인다. 오늘 날짜일 때만 의미가 있다 */}
        {Boolean(effectiveRoomId) && date === todayISO() && <ArrivingCard classRoomId={effectiveRoomId} />}

        {/* 반이 없으면 board 쿼리가 비활성인 채 isPending=true — 스피너 대신 이유를 보여 준다 */}
        {rooms.data?.length === 0 && (
          <EmptyState title="아직 반이 없어요" body="운영 화면에서 반을 만들면 출석부를 쓸 수 있어요." />
        )}

        {Boolean(effectiveRoomId) && board.isPending && <Spinner />}
        {board.isError && (
          <ErrorNote message="출석 정보를 불러오지 못했어요." onRetry={() => void board.refetch()} />
        )}

        {board.data?.rows.length === 0 && (
          <EmptyState title={EMPTY_STATES.attendance.title} body="이 반에 편성된 원아가 없어요." />
        )}

        <ul className="flex flex-col gap-2">
          {board.data?.rows.map((row) => (
            <Card key={row.child.id} as="li" className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <Avatar name={row.child.name} size={44} />
                <Link to={`/children/${row.child.id}`} className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{row.child.name}</p>
                  <p className="truncate text-sm text-ink-sub">
                    {row.attendance?.checkInAt
                      ? `${ATTENDANCE_COPY.checkedIn} ${formatTime(row.attendance.checkInAt)}`
                      : ATTENDANCE_COPY.notYet}
                    {row.attendance?.checkOutAt
                      ? ` · ${ATTENDANCE_COPY.checkedOut} ${formatTime(row.attendance.checkOutAt)}`
                      : ''}
                  </p>
                  {row.attendance?.reason && (
                    <p className="truncate text-sm text-ink-sub">사유: {row.attendance.reason}</p>
                  )}
                </Link>
                <Chip tone={row.status === 'PRESENT' ? 'primary' : 'neutral'}>
                  {ATTENDANCE_STATUS_LABELS[row.status]}
                </Chip>
              </div>

              {canWrite && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={row.attendance?.checkInAt ? 'outline' : 'primary'}
                    className="flex-1"
                    icon={<LogIn size={15} />}
                    loading={check.isPending}
                    onClick={() => check.mutate({ childId: row.child.id, kind: 'in' })}
                  >
                    등원
                  </Button>
                  <Button
                    size="sm"
                    variant={row.attendance?.checkOutAt ? 'outline' : 'secondary'}
                    className="flex-1"
                    icon={<LogOut size={15} />}
                    loading={check.isPending}
                    onClick={() => check.mutate({ childId: row.child.id, kind: 'out' })}
                  >
                    하원
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    loading={setStatus.isPending}
                    onClick={() => setStatus.mutate({ childId: row.child.id, status: 'ABSENT' })}
                  >
                    결석
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </ul>
      </div>
    </Screen>
  );
}

/** 지금 오는 중인 가정 — 대략적 위치(약 100m)와 마지막 갱신 시각만 보여 준다 (§5.10 v1.22) */
function ArrivingCard({ classRoomId }: { classRoomId: string }) {
  const trips = useQuery({
    queryKey: ['pickups', 'location', 'active', classRoomId],
    queryFn: () => apiData<ActivePickupTrip[]>(`/pickups/location/active?classRoomId=${classRoomId}`),
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  });

  if (!trips.data || trips.data.length === 0) return null;

  return (
    <Card>
      <p className="mb-1 flex items-center gap-1.5 font-semibold">
        <MapPin size={16} className="text-primary-strong" /> {PICKUP_LOCATION_COPY.staffTitle}
      </p>
      <p className="mb-2 text-sm text-ink-sub">{PICKUP_LOCATION_COPY.staffHint}</p>
      <ul className="flex flex-col gap-2">
        {trips.data.map((trip) => (
          <li key={trip.id} className="flex items-center gap-3 rounded-sm bg-bg px-3 py-2">
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">
                {trip.child.name}{' '}
                <span className="font-normal text-ink-sub">
                  · {trip.guardian.name}(
                  {GUARDIAN_RELATION_LABELS[trip.guardian.relation as keyof typeof GUARDIAN_RELATION_LABELS] ??
                    trip.guardian.relation}
                  )
                </span>
              </p>
              <p className="truncate text-sm text-ink-sub">
                {trip.kindLabel}
                {trip.distanceM !== null && ` · ${PICKUP_LOCATION_COPY.distanceToFacility(formatDistance(trip.distanceM))}`}
                {' · '}
                {formatRelative(trip.updatedAt)}
              </p>
            </div>
            <a
              href={mapLink({ lat: trip.lat, lng: trip.lng }, `${trip.child.name} 보호자`)}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 text-sm font-medium text-primary-strong underline"
            >
              {PICKUP_LOCATION_COPY.mapLabel}
            </a>
          </li>
        ))}
      </ul>
    </Card>
  );
}
